import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star } from "lucide-react";

interface Review {
  id: string;
  name: string;
  event: string;
  text: string;
  rating: number;
  sort_order: number;
  is_active: boolean;
}

const emptyReview = { name: "", event: "", text: "", rating: 5, sort_order: 0, is_active: true };

export default function LandingReviews() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<Review> | null>(null);
  const [open, setOpen] = useState(false);

  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ["landing_reviews"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("landing_reviews")
        .select("*")
        .order("sort_order");
      if (error) throw error;
      return data as Review[];
    },
  });

  const save = useMutation({
    mutationFn: async (r: Partial<Review>) => {
      if (r.id) {
        const { error } = await supabase.from("landing_reviews").update(r).eq("id", r.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("landing_reviews").insert([r as any]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["landing_reviews"] });
      setOpen(false);
      setEditing(null);
      toast.success("Сохранено");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("landing_reviews").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["landing_reviews"] });
      toast.success("Удалено");
    },
  });

  const openEdit = (r?: Review) => {
    setEditing(r ? { ...r } : { ...emptyReview });
    setOpen(true);
  };

  return (
    <AdminLayout title="Отзывы на лендинге">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground text-sm">Управление отзывами, которые отображаются на главной странице</p>
        <Button onClick={() => openEdit()} size="sm"><Plus className="w-4 h-4 mr-1" />Добавить</Button>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">#</TableHead>
              <TableHead>Имя</TableHead>
              <TableHead>Событие</TableHead>
              <TableHead>Рейтинг</TableHead>
              <TableHead>Активен</TableHead>
              <TableHead className="w-24">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Загрузка...</TableCell></TableRow>
            ) : reviews.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.sort_order}</TableCell>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell>{r.event}</TableCell>
                <TableCell><div className="flex">{Array.from({length: r.rating}).map((_,i) => <Star key={i} className="w-3 h-3 fill-primary text-primary" />)}</div></TableCell>
                <TableCell>{r.is_active ? "✅" : "❌"}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(r)}><Pencil className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => { if(confirm("Удалить?")) remove.mutate(r.id); }}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Редактировать отзыв" : "Новый отзыв"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Имя</Label><Input value={editing?.name || ""} onChange={e => setEditing(p => ({...p!, name: e.target.value}))} /></div>
            <div><Label>Событие</Label><Input value={editing?.event || ""} onChange={e => setEditing(p => ({...p!, event: e.target.value}))} /></div>
            <div><Label>Текст отзыва</Label><Textarea rows={4} value={editing?.text || ""} onChange={e => setEditing(p => ({...p!, text: e.target.value}))} /></div>
            <div className="flex gap-4">
              <div className="flex-1"><Label>Рейтинг (1-5)</Label><Input type="number" min={1} max={5} value={editing?.rating || 5} onChange={e => setEditing(p => ({...p!, rating: +e.target.value}))} /></div>
              <div className="flex-1"><Label>Порядок</Label><Input type="number" value={editing?.sort_order || 0} onChange={e => setEditing(p => ({...p!, sort_order: +e.target.value}))} /></div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={editing?.is_active ?? true} onCheckedChange={v => setEditing(p => ({...p!, is_active: v}))} />
              <Label>Активен</Label>
            </div>
            <Button className="w-full" onClick={() => editing && save.mutate(editing)} disabled={save.isPending}>
              {save.isPending ? "Сохранение..." : "Сохранить"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
